/**
 * Card-Familie — Sprint-16-Brand.
 *
 * Varianten:
 *   - default: bg-card (Cream) + hauchdünner Line-Border + shadow-card
 *   - dark:    bg-navy + paper-Text (Feature-Card, z.B. Bookings-Sidebar 4a, Preise-Card 4c)
 *   - alt:     bg-card-alt (subtiler Cream-Shift, Form-Section-BG in 4e)
 *
 * Kein Hover-Lift — Mockups zeigen ruhige Cards ohne Motion. Wenn eine Card
 * klickbar ist, macht der Consumer `cursor-pointer + hover:bg-card-alt` inline.
 *
 * Semantik-Shift ab Sprint 16: CardTitle ist ein small-uppercase-eyebrow-Label
 * (Mockup-Pattern), NICHT mehr eine Serif-h3-Überschrift. Für Serif-Big-Titles
 * in einer Card den Consumer inline mit `<h2 className="font-serif text-2xl">`.
 */
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

const cardVariants = cva(['rounded-lg overflow-hidden'], {
  variants: {
    variant: {
      default: 'bg-card border border-line/50 shadow-card text-ink',
      dark: 'bg-navy text-paper shadow-card',
      alt: 'bg-card-alt border border-line/50 shadow-card text-ink',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
  ),
);
Card.displayName = 'Card';

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-2 px-6 pb-2 pt-5', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

/** Small-uppercase-eyebrow-Label. Mockup: „STAMMDATEN", „BELEGUNG · 14 TAGE". */
export const CardTitle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'text-ink-soft text-[11px] font-semibold uppercase tracking-[0.12em]',
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-ink-soft text-sm', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('px-6 pb-6', className)} {...props} />
  ),
);
CardBody.displayName = 'CardBody';

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-2 px-6 pb-5 pt-3', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

export { cardVariants };
